import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  FormLabel,
  Pagination,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { getSendApprovedVisitorListAPI, getVisitRequestHistoryAPI } from 'apis/visitRequest/visitRequestAPI';
import VisitRequestButton from './VisitRequestStatusBtn';
import './VisitRequestDetails.css';
import { useNavigate } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { BrowserNotSupported } from '@mui/icons-material';
import useUserStore from '../../store/useUserStore';
import MailIcon from '@mui/icons-material/Mail';
import SnackbarAlert from 'components/SnackbarAlert';
import { useSnackbarStore } from 'store/useSnackbarStore';

// 방문자 정보 데이터 인터페이스
export interface TVisitor {
  id: number;
  companyName: string;
  companyPhoneNumber: string;
  name: string;
  phoneNumber: string;
  birth: string;
  bloodType: string;
  carNumber: string;
  isVip?: boolean;
  note: string;
  digitalDevices?: TDigitalDevices[];
}

export interface TDigitalDevices {
  id: number;
  digitalDeviceId: number;
  modelName: string;
  serialNumber: string;
  note: string;
}

// 신청자 데이터 인터페이스
export interface TRequester {
  name: string;
  birth: string;
  phoneNumber: string;
  companyName: string;
  companyPhoneNumber: string;
}

interface TBusinessPlaces {
  id: number;
  name: string;
}

interface TVisitRequestsBasic {
  securityPledge?: any;
  securityPolicyId?: string;
  requester: TRequester;
  businessPlaces: TBusinessPlaces[];
  digitalDevices: TDigitalDevices[];
}

export const resetData = {
  id: 0,
  companyName: '',
  companyPhoneNumber: '',
  name: '',
  phoneNumber: '',
  birth: '',
  bloodType: '',
  carNumber: '',
  isVip: false,
  note: ''
};

//** 내방신청 내역 페이지**//
export default function VisitRequestDetails() {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().subtract(1, 'month'), // 방문일자 시작은 한달전
    dayjs().add(1, 'year') // 방문일자 종료는 한달전 기준 1년 후
  ]);
  const [status, setStatus] = useState<string>(''); // 상태 ('' : 전체 | )
  const [managerName, setManagerName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [visitRequests, setVisitRequests] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1); // 페이지네이션
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const requestsPerPage = 10; // 한 페이지에 표시할 요청 수
  const scrollBoxRef = useRef<HTMLDivElement>(null);
  // 다크 라이트 모드
  const theme = useTheme();
  const isXSmall = useMediaQuery(theme.breakpoints.only('xs'));
  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  // 스낵바
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleSearchClick = async () => {
    // 조회 버튼을 클릭하면 페이지 초기화
    setCurrentPage(1);
    await handleSubmit();
  };

  // API 호출을 통한 요청 조회
  const handleSubmit = async () => {
    // 날짜 유효성 검사
    if (!dates[0] || !dates[1]) {
      alert('방문일자를 선택해주세요.');
      return;
    }

    // 모든 입력이 유효할 경우 getVisitRequestHistoryAPI 호출
    try {
      const requestData = {
        visitFrom: dates[0].format('YYYY-MM-DD'), // 시작 날짜를 'YYYY-MM-DD' 형식으로 변환
        visitTo: dates[1].format('YYYY-MM-DD'), // 종료 날짜를 'YYYY-MM-DD' 형식으로 변환
        managerName: managerName || null, // 빈 문자열일 경우 null로 설정
        status: status || null, // 빈 문자열일 경우 null로 설정 (백 요청)
        page: currentPage
      };
      // console.log('API requestData:', requestData);
      setIsLoading(true);
      const response = await getVisitRequestHistoryAPI(requestData);
      // console.log('API 응답:', response.data);
      setVisitRequests(response.data.visitRequests);
      setTotalElements(response.data.totalEelements); // 총 요청 수
      setTotalPages(response.data.totalPages);
    } catch (error) {
      // console.error('방문 신청 중 오류 발생:', error);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const [sentVisitorList, setSentVisitorList] = useState<boolean>(false);
  const handleSendApprovedVisitorList = async () => {
    try {
      setSentVisitorList(true);

      await getSendApprovedVisitorListAPI().then((result) => {
        if (result.code == 200) showSnackbar(t('visit_request_list.send_mail_success'), 'success');
        else showSnackbar(t('visit_request_list.send_mail_fail'), 'error');
      });
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setSentVisitorList(false);
      }, 1000);
    }
  };

  // D-day 형식으로 날짜 포맷
  function formatDday(visitFrom: string): string {
    const visitDate = dayjs(visitFrom);
    const today = dayjs();
    const diff = visitDate.diff(today, 'day');
    if (diff > 0) return `D-${diff}`;
    if (diff < 0) return `D+${Math.abs(diff)}`;
    return 'D-day';
  }

  // 날짜 변경 핸들러
  const handleDateChange = (newDate: Dayjs | null, type: 'start' | 'end') => {
    setDates((prevDates) => {
      return type === 'start' ? [newDate, prevDates[1]] : [prevDates[0], newDate];
    });
  };

  // 상태 변경 핸들러
  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value); // 페이지 변경 시 현재 페이지 업데이트
  };

  // 신청내역 클릭하면 내방신청 페이지로 이동
  const handleBoxClick = async (visitRequest: any) => {
    // const data = await getVisitRequestIdAPI(visitRequest.id);
    // setSelectedVisit(data.data);
    // setSelectedVisitId(visitRequest.id);
    // console.log('선택한 신청내역 정보 handleBoxClick : ', visitRequest);
    // console.log('선택한 신청내역 정보 handleBoxClick data : ', data.data);
    navigate(`/visit/request/${visitRequest.id}`); // 내방신청 페이지로 이동
  };

  // 상태 변경 시에는 따로 빼서 기존 페이지가 1이면 조회, 페이지가 1이 아니면 1로 변경해서 아래 useEffect를 타게 변경
  useEffect(() => {
    if (currentPage == 1) {
      handleSubmit();
    } else {
      setCurrentPage(1);
    }
  }, [status]);

  // currentPage가 변경될 때마다 호출
  useEffect(() => {
    handleSubmit();
  }, [currentPage, language]);

  // 컴포넌트 실행될때마다 조회 돌아야하는데, 안도는듯?
  // useEffect(() => {
  //   handleSubmit();
  // }, []);

  useEffect(() => {
    if (scrollBoxRef.current) {
      scrollBoxRef.current.scrollTo(0, 0);
    }
  }, [visitRequests]);

  // visitRequests 배열을 내림차순 정렬
  const sortedVisitRequests = useMemo(() => {
    return [...visitRequests].sort((a, b) => {
      const dateA = new Date(a.visitFrom).getTime();
      const dateB = new Date(b.visitFrom).getTime();
      return dateB - dateA; // 내림차순 정렬
    });
  }, [visitRequests]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localStorage.getItem('language') || 'ko'}>
      <SnackbarAlert />

      <Box padding={2} display="flex" flexDirection="column" gap={2} height="100%" sx={{ width: { xs: '100vw', sm: '100%' } }}>
        {/* 필터쪽 */}
        <Box borderRadius={1} bgcolor={theme.palette.contentBlock.background} padding={2} display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 1, sm: 4 }} flexWrap="wrap">
            <Box display="flex" flexDirection="row" alignItems="center" gap={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('visit_request_list.visit_date')}
                <FormLabel required sx={{ fontSize: '0.75rem' }} />
              </Typography>
              <DatePicker
                format="YYYY-MM-DD"
                value={dates[0]}
                onChange={(newValue) => handleDateChange(newValue, 'start')}
                slotProps={{
                  calendarHeader: {
                    format: 'YYYY-MM'
                  },
                  textField: { placeholder: t('datepicker_placeholder') || 'YYYY-MM-DD' }
                }}
                sx={{
                  flexGrow: 1,
                  width: '140px',
                  bgcolor: theme.palette.fieldText.background,
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem' // 아이콘 크기 조정
                  }
                }}
              />
              <Box margin="auto 4px">~</Box>
              <DatePicker
                format="YYYY-MM-DD"
                value={dates[1]}
                onChange={(newValue) => handleDateChange(newValue, 'end')}
                slotProps={{
                  calendarHeader: {
                    format: 'YYYY-MM'
                  },
                  textField: { placeholder: t('datepicker_placeholder') || 'YYYY-MM-DD' }
                }}
                sx={{
                  flexGrow: 1,
                  width: '140px',
                  bgcolor: theme.palette.fieldText.background,
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem' // 아이콘 크기 조정
                  }
                }}
              />
            </Box>

            <Box display="flex" flexDirection="row" alignItems="center" gap={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <ButtonGroup size="medium" color="inherit" fullWidth={isXSmall}>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: '#aaa',
                    bgcolor: status === '' ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: status === '' ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => setStatus('')}
                >
                  {t('visit_request_list.status_list.all')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: '#aaa',
                    bgcolor: status === 'REQUESTED' ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: status === 'REQUESTED' ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => setStatus('REQUESTED')}
                >
                  {t('visit_request_list.status_list.request')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: '#aaa',
                    bgcolor: status === 'APPROVED' ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: status === 'APPROVED' ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => setStatus('APPROVED')}
                >
                  {t('visit_request_list.status_list.approve')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: '#aaa',
                    bgcolor: status === 'REJECTED' ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: status === 'REJECTED' ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => setStatus('REJECTED')}
                >
                  {t('visit_request_list.status_list.reject')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: '#aaa',
                    bgcolor: status === 'CANCELED' ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: status === 'CANCELED' ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => setStatus('CANCELED')}
                >
                  {t('visit_request_list.status_list.cancel')}
                </Button>
              </ButtonGroup>
            </Box>

            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title} height={32} lineHeight={2.75}>
                {t('visit_request_list.manager_name')}
                <FormLabel sx={{ fontSize: '0.75rem' }} />
              </Typography>
              <TextField
                value={managerName}
                onChange={(e) => setManagerName(e.target.value)}
                // margin="normal"
                // onBlur={handleManagerBlur} // 포커스를 벗어날 때 유효성 검사 실행
                //  error={managerTouched && !!managerErrMsg} // 필드가 떠난 후에만 에러 표시
                //  helperText={managerTouched && managerErrMsg} // 에러 메시지 표시
                sx={{ flexGrow: 1, width: '100px', bgcolor: theme.palette.fieldText.background }}
                type="text"
                size="small"
                variant="outlined"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchClick();
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ borderRadius: '4px', fontWeight: 'bold', width: '100px', height: '30px', margin: 'auto 0px' }}
                onClick={handleSearchClick} // 버튼 클릭 시 handleSubmit 함수 호출
              >
                {t('visit_request_list.search')}
              </Button>
            </Box>

            {user?.groups.some((group) => group.type === 'EMPLOYEE') && (
              // 승인목록 메일전송
              <Box display="flex" flexDirection="row" alignItems="center" flexGrow={1} justifyContent="flex-end">
                <Button
                  sx={{ flexGrow: { xs: 1, sm: 0 }, paddingX: 1, marginTop: { xs: 0, sm: -1 }, marginBottom: { xs: -1, sm: -1 } }}
                  size="small"
                  onClick={handleSendApprovedVisitorList}
                  disabled={sentVisitorList}
                >
                  <MailIcon sx={{ marginRight: '4px' }} /> {/* 아이콘과 텍스트 간격 */}
                  <Box sx={{ minWidth: '60px' }}>
                    {t('visit_request_list.send_mail')
                      .split('_')
                      .map((word, index) => (
                        <span key={index} style={{ display: 'block' }}>
                          {word}
                        </span> // 각 단어를 block으로 설정
                      ))}
                  </Box>
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* 내방신청 내역 */}
        <Box
          borderRadius={1}
          bgcolor={theme.palette.contentBlock.background}
          padding={2}
          display="flex"
          flexDirection="column"
          gap={2}
          flexGrow={1}
          height="100%"
        >
          <Typography variant="h5" fontWeight="bold">
            {t('visit_request_list.visit_request_list')}
          </Typography>
          {isLoading ? (
            <Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : sortedVisitRequests.length > 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              padding="2px"
              sx={{ height: 'calc(100vh - 409px)', minHeight: '200px', overflowY: 'auto' }}
              ref={scrollBoxRef}
            >
              {sortedVisitRequests.map((visitRequest: any, index: number) => (
                <Box
                  key={index}
                  borderRadius={1}
                  bgcolor={theme.palette.contentBlock.main}
                  boxShadow={1}
                  padding={2}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? '#404040' : '#f0f0f0',
                      outline: '1px solid #c4c4c4',
                      shadow: 'none'
                    },
                    ...(visitRequest.status === 'CANCELED' && { opacity: 0.5 })
                  }}
                  onClick={() => handleBoxClick(visitRequest)} // Box 클릭 시 선택된 정보 저장
                >
                  <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    {/* 방문 요청 상태에 따른 버튼 표시  - 공통컴포넌트로 뺌*/}
                    <VisitRequestButton
                      status={
                        visitRequest.status == 'APPROVED' && visitRequest.totalVisitors == visitRequest.cards.length ? 'REQUEST_COMPLETED' : visitRequest.status
                      }
                    />
                    <Typography variant="body1" fontWeight="bold" color="primary.dark">
                      {visitRequest.visitFrom} ~ {visitRequest.visitTo}
                    </Typography>
                    <Chip label={formatDday(visitRequest.visitFrom)} size="small" color="default" />
                  </Box>

                  <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center" gap={1}>
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexGrow={1} width="100%">
                      <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request_list.manager_name')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '100px' }}>
                        {visitRequest.managerName || '알 수 없는 담당자'} / {visitRequest.businessPlaceName || '알 수 없는 사업장'}
                      </Typography>
                    </Box>

                    <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexGrow={1} width="100%">
                      <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request_list.requester')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '100px' }}>
                        {visitRequest.requesterName} / {visitRequest.requesterCompanyName}
                      </Typography>
                    </Box>

                    <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexGrow={1} width="100%">
                      <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                        {t('visit_request_list.card_number')}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ flexGrow: 1, width: '100px' }}
                        color={visitRequest.totalVisitors == visitRequest.cards.length ? 'textPrimary' : 'error'}
                      >
                        {visitRequest.status === 'APPROVED' &&
                          (visitRequest.totalVisitors == visitRequest.cards.length
                            ? visitRequest.cards[0]
                            : `${visitRequest.cards.length} / ${visitRequest.totalVisitors} ${t('visit_request_list.person_matches')}`)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_list.purpose')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '100px' }}>
                      {visitRequest.purpose}
                    </Typography>
                  </Box>

                  {visitRequest.status === 'REJECTED' && visitRequest.rejectedReason && (
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Typography width={80} variant="caption" color="error">
                        {t('visit_request_list.reject_reason')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '100px' }}>
                        {visitRequest.rejectedReason}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={2} sx={{ userSelect: 'none' }}>
              <BrowserNotSupported sx={{ color: theme.palette.mode == 'dark' ? 'lightgrey' : 'gray', fontSize: 100 }} />
              <Typography sx={{ color: theme.palette.mode == 'dark' ? 'lightgrey' : 'gray', textAlign: 'center' }}>
                {t('visit_request_list.no_request_data')}
              </Typography>
            </Box>
          )}

          {totalPages > 1 && ( // count가 1보다 클 때만 렌더링
            <Box display="flex" justifyContent="center">
              <Pagination
                count={totalPages} // 총 페이지 수
                page={currentPage} // 현재 페이지
                onChange={handlePageChange} // 페이지 변경 핸들러
                showFirstButton
                showLastButton
                size="small"
                sx={{ height: 26 }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
